import { computed, nextTick, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { smsAiDraftApiAdapter } from '@/components/sms/aiDraft/smsAiDraftApiAdapter.ts';
import { AiDraftFailureReason, SmsAiDraftState } from '@/components/sms/aiDraft/types.ts';
import { useAiDraftFeedback } from '@/components/aiDraft/feedback/useAiDraftFeedback.ts';
import type {
    SmsAiDraftAdapter,
    SmsAiDraftFeedback,
    SmsAiDraftGeneratedDraft,
    SmsAiDraftLocale,
    SmsAiDraftSnapshot,
    UpdateSmsAiDraftResult,
} from '@/components/sms/aiDraft/types.ts';

export { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
export { AiDraftFailureReason, SmsAiDraftState } from '@/components/sms/aiDraft/types.ts';
export type {
    SmsAiDraftAdapter,
    SmsAiDraftFeedback,
    SmsAiDraftFeedbackSubmission,
    SmsAiDraftGeneratedDraft,
    SmsAiDraftGeneratePayload,
    SmsAiDraftCommonResult,
    SmsAiDraftLocale,
    SmsAiDraftSnapshot,
    SmsAiDraftUpdatePayload,
    UpdateSmsAiDraftResult,
} from '@/components/sms/aiDraft/types.ts';

interface GenerateOptions {
    locale: SmsAiDraftLocale;
    snapshot: SmsAiDraftSnapshot;
    applyDraft: (draft: SmsAiDraftGeneratedDraft) => boolean;
}

interface UpdateOptions {
    locale: SmsAiDraftLocale;
    snapshot: SmsAiDraftSnapshot;
    text: string;
    instruction: string;
    applyDraft: (draft: SmsAiDraftGeneratedDraft) => boolean;
}

interface UseSmsAiDraftOptions {
    adapter?: SmsAiDraftAdapter;
}

interface CompleteDraftRequestOptions {
    applyDraft: (draft: SmsAiDraftGeneratedDraft) => boolean;
    currentRequestId: number;
    interactionId: number;
    nextGeneratedDraft: SmsAiDraftGeneratedDraft;
    nextSnapshot: SmsAiDraftSnapshot;
}

interface DraftRequestHandle {
    currentRequestId: number;
    currentBackendRequestId: string;
    previousDraftState: PreviousSmsDraftState;
}

type SmsAiDraftFailureResult = Extract<UpdateSmsAiDraftResult, { ok: false }>;
type SmsMappedDraftFailureResult = Exclude<
    SmsAiDraftFailureResult,
    { reason: typeof AiDraftFailureReason.Unsupported }
>;
type SmsMappedFailureReason = SmsMappedDraftFailureResult['reason'];

interface PreviousSmsDraftState {
    feedback: SmsAiDraftFeedback;
    generatedDraft: SmsAiDraftGeneratedDraft | null;
}

const DEFAULT_RATE_LIMIT_SECONDS = 60;

const FAILURE_STATE_BY_REASON: Record<SmsMappedFailureReason, SmsAiDraftState> = {
    [AiDraftFailureReason.RateLimited]: SmsAiDraftState.RateLimited,
    [AiDraftFailureReason.Cancelled]: SmsAiDraftState.Stopped,
    [AiDraftFailureReason.NotEntitled]: SmsAiDraftState.NotEntitled,
    [AiDraftFailureReason.ServiceLead]: SmsAiDraftState.ServiceLead,
};

export const useSmsAiDraft = (options: UseSmsAiDraftOptions = {}) => {
    const adapter = options.adapter ?? smsAiDraftApiAdapter;
    const state = ref<SmsAiDraftState>(SmsAiDraftState.Idle);
    const snapshot = ref<SmsAiDraftSnapshot | null>(null);
    const undoSnapshot = ref<SmsAiDraftSnapshot | null>(null);
    const appliedInteractionId = ref<number | null>(null);
    const requestId = ref(0);
    const backendRequestId = ref<string | null>(null);
    const rateLimitSeconds = ref(DEFAULT_RATE_LIMIT_SECONDS);
    const feedback = ref<SmsAiDraftFeedback>(null);
    const generatedDraft = ref<SmsAiDraftGeneratedDraft | null>(null);

    const {
        feedbackInteractionId,
        clearFeedbackTarget,
        setFeedbackTarget,
        selectFeedback,
        submitFeedback,
    } = useAiDraftFeedback(adapter, feedback);

    const hasGeneratedDraft = computed<boolean>(() => generatedDraft.value !== null);
    const isInProgress = computed<boolean>(() => state.value === SmsAiDraftState.Loading);
    const stateHasGeneratedDraft = computed<boolean>(
        () =>
            state.value === SmsAiDraftState.Inserted ||
            state.value === SmsAiDraftState.UpdateUnsupported
    );
    const canUndo = computed<boolean>(
        () => undoSnapshot.value !== null && state.value !== SmsAiDraftState.Loading
    );
    const showFeedbackControls = computed<boolean>(
        () => hasGeneratedDraft.value && stateHasGeneratedDraft.value
    );
    const attributionInteractionId = computed<number | null>(() => {
        const interactionId = feedbackInteractionId.value;

        return showFeedbackControls.value &&
            interactionId !== null &&
            Number.isInteger(interactionId) &&
            interactionId > 0
            ? interactionId
            : null;
    });

    const requestIsActive = (id: number): boolean =>
        requestId.value === id && state.value !== SmsAiDraftState.Stopped;

    const clearGeneratedState = () => {
        generatedDraft.value = null;
    };

    const returnToIdle = () => {
        state.value = SmsAiDraftState.Idle;
        feedback.value = null;
        clearGeneratedState();
        clearFeedbackTarget();
        snapshot.value = null;
        undoSnapshot.value = null;
    };

    const startDraftRequest = (
        nextSnapshot: SmsAiDraftSnapshot,
        preserveFeedbackTarget = false
    ): DraftRequestHandle => {
        const currentRequestId = requestId.value + 1;
        const currentBackendRequestId = uuidv4();
        const previousDraftState = {
            feedback: feedback.value,
            generatedDraft: generatedDraft.value,
        };

        requestId.value = currentRequestId;
        backendRequestId.value = currentBackendRequestId;
        snapshot.value = nextSnapshot;
        state.value = SmsAiDraftState.Loading;
        feedback.value = null;
        clearGeneratedState();

        if (!preserveFeedbackTarget) {
            clearFeedbackTarget();
        }

        return { currentRequestId, currentBackendRequestId, previousDraftState };
    };

    const completeDraftRequest = async ({
        applyDraft,
        currentRequestId,
        interactionId,
        nextGeneratedDraft,
        nextSnapshot,
    }: CompleteDraftRequestOptions) => {
        backendRequestId.value = null;

        if (!applyDraft(nextGeneratedDraft)) {
            returnToIdle();

            return;
        }

        await nextTick();

        if (!requestIsActive(currentRequestId)) {
            return;
        }

        undoSnapshot.value = { text: nextSnapshot.text };
        appliedInteractionId.value = interactionId > 0 ? interactionId : null;
        generatedDraft.value = nextGeneratedDraft;
        clearFeedbackTarget();
        setFeedbackTarget(interactionId);
        state.value = SmsAiDraftState.Inserted;
    };

    const handleDraftRequestFailure = (
        failure: SmsMappedDraftFailureResult,
        currentRequestId: number
    ) => {
        if (!requestIsActive(currentRequestId)) {
            return;
        }

        backendRequestId.value = null;
        clearGeneratedState();
        clearFeedbackTarget();

        if (failure.reason === AiDraftFailureReason.RateLimited) {
            rateLimitSeconds.value = failure.rateLimitSeconds;
        }

        state.value = FAILURE_STATE_BY_REASON[failure.reason];
    };

    const handleUnsupportedDraftRequest = (
        currentRequestId: number,
        previousDraftState: PreviousSmsDraftState
    ) => {
        if (!requestIsActive(currentRequestId)) {
            return;
        }

        backendRequestId.value = null;
        snapshot.value = null;
        feedback.value = previousDraftState.feedback;
        generatedDraft.value = previousDraftState.generatedDraft;
        state.value = SmsAiDraftState.UpdateUnsupported;
    };

    const handleUnexpectedDraftRequestError = (currentRequestId: number) => {
        if (!requestIsActive(currentRequestId)) {
            return;
        }

        backendRequestId.value = null;
        clearGeneratedState();
        clearFeedbackTarget();
        state.value = SmsAiDraftState.Failed;
    };

    const cancelBackendRequest = (idToCancel: string | null) => {
        if (!idToCancel) {
            return;
        }

        try {
            Promise.resolve(adapter.stop(idToCancel)).catch(() => {});
        } catch {
            // Best-effort cancellation must not block local composer recovery.
        }
    };

    const generate = async ({ locale, snapshot: nextSnapshot, applyDraft }: GenerateOptions) => {
        const { currentRequestId, currentBackendRequestId } = startDraftRequest(nextSnapshot);

        try {
            const result = await adapter.generate({ locale, requestId: currentBackendRequestId });

            if (!requestIsActive(currentRequestId)) {
                return;
            }

            if (!result.ok) {
                handleDraftRequestFailure(result, currentRequestId);

                return;
            }

            await completeDraftRequest({
                applyDraft,
                currentRequestId,
                interactionId: result.interactionId,
                nextGeneratedDraft: {
                    locale,
                    text: result.text,
                },
                nextSnapshot,
            });
        } catch {
            handleUnexpectedDraftRequestError(currentRequestId);
        }
    };

    const update = async ({
        locale,
        snapshot: nextSnapshot,
        text,
        instruction,
        applyDraft,
    }: UpdateOptions) => {
        const { currentRequestId, currentBackendRequestId, previousDraftState } = startDraftRequest(
            nextSnapshot,
            true
        );

        try {
            const result = await adapter.update({
                locale,
                requestId: currentBackendRequestId,
                text,
                instruction,
            });

            if (!requestIsActive(currentRequestId)) {
                return;
            }

            if (!result.ok) {
                if (result.reason === AiDraftFailureReason.Unsupported) {
                    handleUnsupportedDraftRequest(currentRequestId, previousDraftState);

                    return;
                }

                handleDraftRequestFailure(result, currentRequestId);

                return;
            }

            await completeDraftRequest({
                applyDraft,
                currentRequestId,
                interactionId: result.interactionId,
                nextGeneratedDraft: {
                    locale,
                    text: result.text,
                },
                nextSnapshot,
            });
        } catch {
            handleUnexpectedDraftRequestError(currentRequestId);
        }
    };

    const stop = async (restoreSnapshot: (snapshot: SmsAiDraftSnapshot) => void) => {
        requestId.value += 1;

        const idToCancel = backendRequestId.value;
        backendRequestId.value = null;

        if (snapshot.value) {
            restoreSnapshot(snapshot.value);
        }

        clearGeneratedState();
        clearFeedbackTarget();
        feedback.value = null;
        undoSnapshot.value = null;
        state.value = SmsAiDraftState.Stopped;
        cancelBackendRequest(idToCancel);
    };

    const reset = () => {
        const wasLoading = state.value === SmsAiDraftState.Loading;
        const idToCancel = backendRequestId.value;

        requestId.value += 1;
        backendRequestId.value = null;

        if (wasLoading) {
            cancelBackendRequest(idToCancel);
        }

        returnToIdle();
        appliedInteractionId.value = null;
        rateLimitSeconds.value = DEFAULT_RATE_LIMIT_SECONDS;
    };

    const undo = (restoreSnapshot: (snapshot: SmsAiDraftSnapshot) => void) => {
        if (!canUndo.value || !undoSnapshot.value) {
            return;
        }

        requestId.value += 1;
        backendRequestId.value = null;
        restoreSnapshot(undoSnapshot.value);
        appliedInteractionId.value = null;
        returnToIdle();
    };

    const clearGeneratedDraft = () => {
        requestId.value += 1;
        backendRequestId.value = null;
        returnToIdle();
        appliedInteractionId.value = null;
    };

    return {
        appliedInteractionId,
        attributionInteractionId,
        canUndo,
        clearGeneratedDraft,
        feedback,
        generate,
        generatedDraft,
        hasGeneratedDraft,
        isInProgress,
        rateLimitSeconds,
        reset,
        selectFeedback,
        showFeedbackControls,
        state,
        stop,
        submitFeedback,
        undo,
        update,
    };
};
